import { useEffect, useMemo, useState } from "react";
import Layout from "../../../components/custom/Layout";
import { Nav } from "reactstrap";
import CustomNavItem from "../../../components/custom/CustomNavItem";
import { TabContent } from "reactstrap";
import CustomTabPane from "../../../components/custom/CustomTabPane";
import SendMessage from "./SendMessage";
import ManageGroups from "./ManageGroups";
import { ChoiceEmployee, MessageGroupData } from "../../../globals/interfaces";
import apiClient from "../../../apiClient";
import { useAppContext } from "../../../context";
import { getTsDate, notify } from "../../../utils";
import _ from "lodash";
import Log from "./Log";

export type Tab = 'SEND_MESSAGE' | 'MANAGE_GROUPS' | 'LOG';
export type CreateGroup = (name: string) => Promise<void>;
export type UpdateGroup = (name: string, id: number) => Promise<void>;
export type DeleteGroup = (id: number) => Promise<void>;
export type AddGroupMember = (messageGroupId: number, employeeId: number) => Promise<void>;
export type DeleteGroupMember = (messageGroupId: number, id: number) => Promise<void>;

export default function MessageGroups() {
    const { user } = useAppContext();
    const [activeTab, setActiveTab] = useState<Tab>('SEND_MESSAGE');
    const [groups, setGroups] = useState<Array<MessageGroupData>>([]);
    const [employees, setEmployees] = useState<Array<ChoiceEmployee>>([]);
    const [selectedGroup, setSelectedGroup] = useState<MessageGroupData>();
    const [tableIndex, setTableIndex] = useState<number>();
    const selectedGroupMembersSet = useMemo(() => {
        const set = new Set();
        if (selectedGroup && selectedGroup.members) {
            const { members } = selectedGroup;
            for (const member of members) {
                set.add(member.employeeId);
            }
        }
        return set;
    }, [selectedGroup]);

    useEffect(() => {
        const getData = async () => {
            try {
                const res = await apiClient.get(`/message-groups`);
                const { groups, employees } = res.data;
                setGroups(groups);
                setEmployees(employees);
            } catch (err) {
                alert(err);
            }
        }
        getData();
    }, []);

    const createGroup: CreateGroup = async (name: string) => {
        try {
            const res = await apiClient.post(`/message-groups`, {
                name,
                createdBy: user.s_email,
                createdAt: getTsDate(),
                modifiedAt: getTsDate(),
                modifiedBy: user.s_email,
            });
            setGroups(prev => {
                const copy = _.cloneDeep(prev);
                copy.push(res.data);
                return copy;
            });
            setTableIndex(groups.length + 1);
            setSelectedGroup(res.data);
        } catch (err) {
            alert(err);
        }
    }

    const updateGroup: UpdateGroup = async (name: string, id: number) => {
        try {
            const res = await apiClient.put(`/message-groups/${id}`, {
                name,
                modifiedAt: getTsDate(),
                modifiedBy: user.s_email,
            });
            setGroups(prev => {
                const copy = _.cloneDeep(prev);
                const updateIndex = copy.findIndex(item => item.id === id);
                copy[updateIndex].name = name;
                return copy;
            });
        } catch (err) {
            alert(err);
        }
    }

    const deleteGroup: DeleteGroup = async (id: number) => {
        try {
            const res = await apiClient.delete(`/message-groups/${id}`);
            setGroups(prev => {
                const filtered = prev.filter(item => item.id !== id);
                return filtered;
            });
        } catch (err) {
            alert(err);
        }
    }

    const addGroupMember: AddGroupMember = async (messageGroupId: number, employeeId: number) => {
        if (selectedGroupMembersSet.has(employeeId)) {
            notify(`This employee already exists in group ${selectedGroup?.name}`, 'error');
            return;
        }
        try {
            const res = await apiClient.post(`/message-groups/member`, {
                messageGroupId,
                employeeId,
                createdBy: user.s_email,
                createdAt: getTsDate(),
            });
            setGroups(prev => {
                const copy = _.cloneDeep(prev);
                const updateIndex = copy.findIndex(item => item.id === messageGroupId);
                copy[updateIndex].members.push(res.data);;
                return copy;
            });
            setSelectedGroup(prev => {
                if (!prev) return prev;
                const copy = _.cloneDeep(prev);
                copy.members.push(res.data);;
                return copy;
            });
            notify('Group member added');
        } catch (err) {
            alert(err);
        }
    }

    const deleteGroupMember: DeleteGroupMember = async (messageGroupId: number, id: number) => {
        try {
            const res = await apiClient.delete(`/message-groups/member/${id}`);
            setGroups(prev => {
                const copy = _.cloneDeep(prev);
                const groupIndex = copy.findIndex(group => group.id === messageGroupId);
                const filtered = copy[groupIndex].members.filter(item => item.id !== id);
                copy[groupIndex].members = filtered;
                return copy;
            });
            setSelectedGroup(prev => {
                if (!prev) return prev;
                const copy = _.cloneDeep(prev);
                const filtered = copy.members.filter(item => item.id !== id);
                copy.members = filtered;
                return copy;
            });
            notify('Group member removed');
        } catch (err) {
            alert(err);
        }
    }

    const sendMessage = async (message: string) => {
        try {
            const res = await apiClient.post(`/message-groups/message`, {
                selectedGroup,
                message,
                date: getTsDate()
            });
            notify('Message sent');
        } catch (err) {
            alert(err);
        }
    }

    const toggleTab = (tab: Tab) => setActiveTab(tab);

    return (
        <Layout>
            <h4>Message Groups</h4>
            <Nav tabs className={'separator-tabs'}>
                <CustomNavItem<Tab>
                    tabName="Send a Message"
                    tabId="SEND_MESSAGE"
                    activeTabId={activeTab}
                    toggleTab={toggleTab}
                />
                <CustomNavItem<Tab>
                    tabName="Manage Groups"
                    tabId="MANAGE_GROUPS"
                    activeTabId={activeTab}
                    toggleTab={toggleTab}
                />
                <CustomNavItem<Tab>
                    tabName="Log"
                    tabId="LOG"
                    activeTabId={activeTab}
                    toggleTab={toggleTab}
                />
            </Nav>
            <TabContent activeTab={activeTab} className={'mt-2'}>
                <CustomTabPane<Tab> tabId={'SEND_MESSAGE'}>
                    <SendMessage 
                        groups={groups}
                        setSelectedGroup={setSelectedGroup}
                        sendMessage={sendMessage}
                    />
                </CustomTabPane>
                <CustomTabPane<Tab> tabId={'MANAGE_GROUPS'}>
                    <ManageGroups 
                        groups={groups}
                        employees={employees}
                        selectedGroup={selectedGroup}
                        setSelectedGroup={setSelectedGroup}
                        createGroup={createGroup}
                        updateGroup={updateGroup}
                        deleteGroup={deleteGroup}
                        addGroupMember={addGroupMember}
                        deleteGroupMember={deleteGroupMember}
                        tableIndex={tableIndex}
                    />
                </CustomTabPane>
                <CustomTabPane<Tab> tabId={'LOG'}>
                    <Log 
                        activeTab={activeTab}
                    />
                </CustomTabPane>
            </TabContent>
        </Layout>
    );
}